import type { AdminUsageSummaryDto, BudgetRights, UsageChip, UsageMemberDto, UsageMembersDto, UsageWorkspaceDto, UsageWorkspacesDto } from '@dbrain/shared';
import { Alert, Badge, Button, Dialog, EmptyState, Icons, Menu, Skeleton } from '@dbrain/ui';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useParams } from 'react-router';
import { api } from '../../api/client.js';
import { useCompanyZone, useMe } from '../../api/hooks.js';
import { BudgetEditor, SourceChip, TodayMeter, TokenInput } from '../../components/admin/Budgets.js';
import { CsvLink, DataTable, FilterChips, FilterMenu, Pagination, PeriodPill, SearchBox, sortState, useListParams, type Column } from '../../components/admin/DataTable.js';
import { ErrorAlert } from '../../components/ErrorAlert.js';
import { formatBytes, formatCount, formatMoney, formatSeconds, formatTokens, quotaResetTime } from '@dbrain/app-core/format';
import { useAdminDomains } from './AccessPage.js';
import { AdminLayout, isOrganizationAdmin, SubTabs } from './AdminLayout.js';
import { FeedbackCard } from './PoliciesPage.js';

/**
 * Yönetim → Kullanım ve limitler (LimitlerOzet, LimitlerUyeler,
 * YonetimKullanim; 06 §23.5): the summary with the default budgets and the
 * domains' budgets, every member against their budget, the workspaces and
 * the answer feedback. Budgets inherit: own → domain → organization →
 * platform; each shows where it comes from. Sums only, never content.
 */
type Tab = 'summary' | 'members' | 'workspaces' | 'feedback';

const usageKey = ['admin', 'usage'] as const;
const DESC = new Set(['today', 'percent', 'month', 'budget', 'input', 'output', 'calls', 'runs', 'sandbox', 'storage', 'cost']);

function useDays(): [number, (days: number) => void] {
  const list = useListParams();
  return [Number(list.get('days') ?? 30) || 30, (days) => list.set({ days: days === 30 ? null : String(days) })];
}

export function UsagePage() {
  const { t } = useTranslation();
  const { tab = '' } = useParams();
  const me = useMe().data;
  const [days, setDays] = useDays();
  if (!me) return null;
  const tabs: Tab[] = ['summary', 'members', 'workspaces', ...(isOrganizationAdmin(me) ? (['feedback'] as const) : [])];
  if (!tabs.includes(tab as Tab)) return <Navigate to={`/admin/usage/summary${days !== 30 ? `?days=${days}` : ''}`} replace />;
  return (
    <AdminLayout section="usage" title={t('admin.sections.usage')} actions={<PeriodPill days={days} onChange={setDays} />}>
      <UsageContent tab={tab as Tab} tabs={tabs} days={days} />
    </AdminLayout>
  );
}

function UsageContent({ tab, tabs, days }: { tab: Tab; tabs: Tab[]; days: number }) {
  const { t } = useTranslation();
  const summary = useQuery({ queryKey: [...usageKey, 'summary', days], queryFn: () => api<AdminUsageSummaryDto>('GET', `/api/admin/usage/summary?days=${days}`) });
  const spaces = useQuery({ queryKey: [...usageKey, 'workspaces', 'count', days], queryFn: () => api<UsageWorkspacesDto>('GET', `/api/admin/usage/workspaces?days=${days}&size=10`), staleTime: 30_000 });
  const keep = days !== 30 ? `?days=${days}` : '';
  const counts: Record<Tab, number | undefined> = { summary: undefined, members: summary.data?.today.members, workspaces: spaces.data?.total, feedback: undefined };
  return (
    <div className="stack admin-usage">
      <SubTabs label={t('admin.usage.tabsLabel')} items={tabs.map((id) => ({ to: `/admin/usage/${id}${keep}`, label: t(`admin.usage.tabs.${id}`), count: counts[id], testId: `usage-tab-${id}` }))} />
      {tab === 'summary' ? <SummaryTab summary={summary.data} error={summary.error} days={days} /> : null}
      {tab === 'members' ? <MembersTab /> : null}
      {tab === 'workspaces' ? <WorkspacesTab /> : null}
      {tab === 'feedback' ? <FeedbackCard days={days} /> : null}
    </div>
  );
}

function useSaveBudgets() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api('PUT', '/api/admin/quotas', body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: usageKey }),
  });
}

function StatCard({ label, value, sub, tone, testId }: { label: ReactNode; value: ReactNode; sub?: ReactNode; tone?: 'danger' | 'warning'; testId: string }) {
  return (
    <div className={`stat-card${tone ? ` stat-card--${tone}` : ''}`} data-testid={testId}>
      <p className="stat-card__label">
        {tone ? <span className={`ui-dot ui-dot--${tone}`} aria-hidden="true" /> : null}
        {label}
      </p>
      <p className="stat-card__value">{value}</p>
      {sub ? <p className="stat-card__sub">{sub}</p> : null}
    </div>
  );
}

function SummaryTab({ summary, error, days }: { summary: AdminUsageSummaryDto | undefined; error: unknown; days: number }) {
  const { t, i18n } = useTranslation();
  const save = useSaveBudgets();
  const [monthly, setMonthly] = useState<string | null>(null);
  const [daily, setDaily] = useState<string | null>(null);
  if (error) return <ErrorAlert error={error} />;
  if (!summary) return <Skeleton block />;
  const { rights } = summary;
  const keep = days !== 30 ? `&days=${days}` : '';
  const platform = t('admin.usage.defaults.platform', { business: formatTokens(summary.defaults.business, i18n.language), developer: formatTokens(summary.defaults.developer, i18n.language) });
  const number = (digits: string | null) => (digits && digits.trim() ? Number(digits) : null);
  const columns: Column<AdminUsageSummaryDto['domains'][number]>[] = [
    {
      id: 'domain',
      header: t('admin.usage.domains.columns.domain'),
      cell: (d) => (
        <span className="person-cell">
          <span className="person-cell__name">{d.name}</span>
          <code className="person-cell__code">{d.code}</code>
        </span>
      ),
    },
    { id: 'members', header: t('admin.usage.domains.columns.members'), width: 'xs', numeric: true, cell: (d) => formatCount(d.members, i18n.language) },
    { id: 'today', header: t('admin.usage.domains.columns.today'), width: 'sm', numeric: true, cell: (d) => formatTokens(d.today, i18n.language) },
    { id: 'period', header: t(`admin.period.d${days}`), width: 'sm', numeric: true, cell: (d) => formatTokens(d.period, i18n.language) },
    {
      id: 'budget',
      header: t('admin.usage.domains.columns.budget'),
      width: 'md',
      cell: (d) => (
        <span className="budget-cell">
          <span className="budget-cell__value" title={d.budget === null ? platform : undefined}>
            {d.budget !== null ? formatTokens(d.budget, i18n.language) : '—'}
          </span>
          <SourceChip source={d.source} />
        </span>
      ),
    },
    {
      id: 'state',
      header: t('admin.usage.domains.columns.state'),
      width: 'md',
      cell: (d) =>
        d.full === 0 && d.near === 0 ? (
          <span className="muted">—</span>
        ) : (
          <span className="state-chips">
            {d.full > 0 ? <Badge tone="danger">{t('admin.usage.domains.full', { count: d.full })}</Badge> : null}
            {d.near > 0 ? <Badge tone="warning">{t('admin.usage.domains.near', { count: d.near })}</Badge> : null}
          </span>
        ),
    },
    {
      id: 'actions',
      header: <span className="ui-visually-hidden">{t('admin.table.actions')}</span>,
      width: 'actions',
      className: 'dt__actions',
      cell: (d) => <DomainActions domain={d} rights={rights} summary={summary} keep={keep} />,
    },
  ];
  return (
    <div className="stack">
      <div className="stat-cards">
        <StatCard
          testId="usage-card-month"
          label={rights.organization ? t('admin.usage.cards.month') : t('admin.usage.cards.monthDomains')}
          value={formatTokens(summary.month.used, i18n.language)}
          sub={summary.month.limit !== null ? t('admin.usage.cards.monthLimit', { limit: formatTokens(summary.month.limit, i18n.language) }) : t('admin.usage.cards.noMonthLimit')}
        />
        <StatCard
          testId="usage-card-today"
          label={t('admin.usage.cards.today')}
          value={`${formatCount(summary.today.active, i18n.language)} / ${formatCount(summary.today.members, i18n.language)}`}
          sub={t('admin.usage.cards.todayUnit')}
        />
        <StatCard
          testId="usage-card-full"
          tone="danger"
          label={t('admin.usage.cards.full')}
          value={formatCount(summary.full, i18n.language)}
          sub={<Link to={`/admin/usage/members?state=full${keep}`} data-testid="usage-card-full-link">{t('admin.usage.cards.show')} →</Link>}
        />
        <StatCard
          testId="usage-card-near"
          tone="warning"
          label={t('admin.usage.cards.near')}
          value={formatCount(summary.near, i18n.language)}
          sub={<Link to={`/admin/usage/members?state=near${keep}`} data-testid="usage-card-near-link">{t('admin.usage.cards.show')} →</Link>}
        />
      </div>

      <section className="admin-card" aria-labelledby="defaults-title" data-testid="usage-defaults">
        <header className="admin-card__head">
          <h2 id="defaults-title">{t('admin.usage.defaults.title')}</h2>
          <p className="muted">{t('admin.usage.defaults.hint')}</p>
        </header>
        <ErrorAlert error={save.error} />
        <form
          className="defaults-form"
          onSubmit={(e) => {
            e.preventDefault();
            void save
              .mutateAsync({
                ...(monthly !== null ? { tenantMonthlyTokens: number(monthly) } : {}),
                ...(daily !== null ? { memberDailyInputTokens: number(daily) } : {}),
              })
              .then(() => {
                setMonthly(null);
                setDaily(null);
              });
          }}
        >
          <label className="defaults-form__field">
            <span className="defaults-form__label">{t('admin.usage.defaults.monthly')}</span>
            {rights.organization ? (
              <TokenInput value={monthly ?? (summary.defaults.monthly !== null ? String(summary.defaults.monthly) : '')} onChange={setMonthly} placeholder={t('admin.usage.defaults.noLimit')} label={t('admin.usage.defaults.monthly')} testId="budget-monthly" />
            ) : (
              <span className="defaults-form__value">{summary.defaults.monthly !== null ? formatTokens(summary.defaults.monthly, i18n.language) : t('admin.usage.defaults.noLimit')}</span>
            )}
          </label>
          <label className="defaults-form__field">
            <span className="defaults-form__label">
              {t('admin.usage.defaults.daily')} <span className="muted">· {t('admin.usage.defaults.dailyUnit')}</span>
            </span>
            {rights.organization ? (
              <TokenInput value={daily ?? (summary.defaults.memberDaily !== null ? String(summary.defaults.memberDaily) : '')} onChange={setDaily} placeholder={platform} label={t('admin.usage.defaults.daily')} testId="budget-daily" />
            ) : (
              <span className="defaults-form__value">{summary.defaults.memberDaily !== null ? formatTokens(summary.defaults.memberDaily, i18n.language) : platform}</span>
            )}
          </label>
          {rights.organization ? (
            <Button type="submit" busy={save.isPending} disabled={monthly === null && daily === null} data-testid="budget-save">
              {t('common.save')}
            </Button>
          ) : null}
        </form>
        {!rights.organization ? (
          <p className="muted" data-testid="budget-domain-hint">
            {t('admin.usage.defaults.readOnly')}
          </p>
        ) : null}
      </section>

      {summary.domains.length > 0 ? (
        <section className="admin-card" aria-labelledby="domains-title" data-testid="usage-domains">
          <header className="admin-card__head">
            <h2 id="domains-title">{t('admin.usage.domains.title')}</h2>
            <p className="muted">{t('admin.usage.domains.hint')}</p>
          </header>
          <DataTable
            label={t('admin.usage.domains.title')}
            columns={columns}
            rows={summary.domains}
            rowKey={(d) => d.id}
            rowTestId={(d) => `usage-domain-${d.code}`}
            className="dt-frame--flat"
          />
          <p className="muted admin-card__foot">{t('admin.usage.domains.footnote')}</p>
        </section>
      ) : null}
    </div>
  );
}

function DomainActions({ domain, rights, summary, keep }: { domain: AdminUsageSummaryDto['domains'][number]; rights: BudgetRights; summary: AdminUsageSummaryDto; keep: string }) {
  const { t, i18n } = useTranslation();
  const save = useSaveBudgets();
  const editable = rights.organization || rights.domains.includes(domain.id);
  const inherited = summary.defaults.memberDaily;
  return (
    <span className="row-actions">
      <Link className="ui-button ui-button--secondary ui-button--sm" to={`/admin/usage/members?domain=${domain.id}${keep}`} data-testid="usage-domain-members">
        {t('admin.usage.domains.members')}
      </Link>
      {editable ? (
        <BudgetEditor
          trigger={t('admin.usage.domains.edit')}
          triggerClassName="ui-button ui-button--secondary ui-button--sm"
          pencil={false}
          triggerLabel={t('admin.usage.domains.editLabel', { name: domain.name })}
          title={t('admin.usage.editor.domainTitle', { name: domain.name })}
          inheritLabel={
            inherited !== null
              ? t('admin.usage.editor.domainInherit', { value: formatTokens(inherited, i18n.language) })
              : t('admin.usage.editor.domainPlatform', { business: formatTokens(summary.defaults.business, i18n.language), developer: formatTokens(summary.defaults.developer, i18n.language) })
          }
          ownLabel={t('admin.usage.editor.domainOwn')}
          current={domain.dailyInputTokens}
          busy={save.isPending}
          error={save.error}
          onSave={(value) => save.mutateAsync({ domains: [{ domainId: domain.id, dailyInputTokens: value }] })}
          testId="usage-domain-editor"
        />
      ) : null}
    </span>
  );
}

const OPTIONAL = ['input', 'output', 'calls', 'runs', 'sandbox', 'storage', 'cost'] as const;
type OptionalColumn = (typeof OPTIONAL)[number];
const COLUMNS_KEY = 'oppy.admin.usage.columns';

function useChosenColumns(): [Set<OptionalColumn>, (column: OptionalColumn, on: boolean) => void] {
  const [chosen, setChosen] = useState<Set<OptionalColumn>>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(COLUMNS_KEY) ?? '[]') as string[];
      return new Set(saved.filter((c): c is OptionalColumn => (OPTIONAL as readonly string[]).includes(c)));
    } catch {
      return new Set();
    }
  });
  return [
    chosen,
    (column, on) => {
      const next = new Set(chosen);
      if (on) next.add(column);
      else next.delete(column);
      setChosen(next);
      try {
        localStorage.setItem(COLUMNS_KEY, JSON.stringify([...next]));
      } catch {
        // the choice lasts for this page only
      }
    },
  ];
}

function MembersTab() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const list = useListParams<'domain' | 'state'>();
  const { sort, onSort } = sortState(list, 'percent', (key) => DESC.has(key));
  const query = list.query();
  const members = useQuery({ queryKey: [...usageKey, 'members', query], queryFn: () => api<UsageMembersDto>('GET', `/api/admin/usage/members?${query}`), placeholderData: keepPreviousData });
  const domains = useAdminDomains();
  const save = useSaveBudgets();
  const [chosen, choose] = useChosenColumns();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulk, setBulk] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const data = members.data;
  const reset = quotaResetTime(i18n.language, new Date(), useCompanyZone());
  const editable = (m: UsageMemberDto) => Boolean(data && (data.rights.organization || m.domains.some((d) => data.rights.domains.includes(d.id))));
  // "Varsayılan · 20 Mn (İnsan Kaynakları, firmanın değeri)"
  const inheritedFrom = (m: UsageMemberDto) => [...m.domains.map((d) => d.name), t(`admin.usage.editor.from.${m.inheritedSource}`)].join(', ');

  const optional: Record<OptionalColumn, Column<UsageMemberDto>> = {
    input: { id: 'input', header: t('admin.usage.members.columns.input'), width: 'sm', numeric: true, sort: 'input', cell: (m) => formatTokens(m.period.input, i18n.language) },
    output: { id: 'output', header: t('admin.usage.members.columns.output'), width: 'sm', numeric: true, sort: 'output', cell: (m) => formatTokens(m.period.output, i18n.language) },
    calls: { id: 'calls', header: t('admin.usage.members.columns.calls'), width: 'sm', numeric: true, sort: 'calls', cell: (m) => formatCount(m.period.calls, i18n.language) },
    runs: { id: 'runs', header: t('admin.usage.members.columns.runs'), width: 'sm', numeric: true, sort: 'runs', cell: (m) => formatCount(m.period.runs, i18n.language) },
    sandbox: { id: 'sandbox', header: t('admin.usage.members.columns.sandbox'), width: 'sm', numeric: true, sort: 'sandbox', cell: (m) => formatSeconds(m.period.sandboxSeconds, i18n.language) },
    storage: { id: 'storage', header: t('admin.usage.members.columns.storage'), width: 'sm', numeric: true, sort: 'storage', cell: (m) => formatBytes(m.period.storageBytes, i18n.language) },
    cost: { id: 'cost', header: t('admin.usage.members.columns.cost'), width: 'sm', numeric: true, sort: 'cost', cell: (m) => formatMoney(m.period.costMicros, 'TRY', i18n.language, m.period.costMicros > 0) },
  };

  const columns: Column<UsageMemberDto>[] = [
    {
      id: 'member',
      header: t('admin.usage.members.columns.member'),
      sort: 'name',
      cell: (m) => (
        <span className="person-cell">
          <span className="person-cell__name">{m.name}</span>
          <span className="person-cell__email">{m.email}</span>
        </span>
      ),
    },
    { id: 'domain', header: t('admin.usage.members.columns.domain'), width: 'md', cell: (m) => (m.domains.length > 0 ? m.domains.map((d) => d.name).join(' · ') : <span className="muted">—</span>) },
    { id: 'today', header: t('admin.usage.members.columns.today'), width: 'xl', sort: 'percent', cell: (m) => <TodayMeter used={m.today} budget={m.budget} state={m.state} /> },
    { id: 'month', header: t('admin.usage.members.columns.month'), width: 'sm', numeric: true, sort: 'month', cell: (m) => formatTokens(m.month, i18n.language) },
    ...OPTIONAL.filter((c) => chosen.has(c)).map((c) => optional[c]),
    {
      id: 'budget',
      header: t('admin.usage.members.columns.budget'),
      width: 'lg',
      sort: 'budget',
      cell: (m) => {
        const shown = (
          <span className="budget-cell">
            <span className="budget-cell__value">{formatTokens(m.budget, i18n.language)}</span>
            <SourceChip source={m.source} />
          </span>
        );
        if (!editable(m)) return shown;
        return (
          <BudgetEditor
            trigger={shown}
            triggerLabel={t('admin.usage.members.editLabel', { name: m.name })}
            title={t('admin.usage.editor.title', { name: m.name })}
            subtitle={t('admin.usage.editor.usedToday', { used: formatTokens(m.today, i18n.language), time: reset })}
            inheritLabel={t('admin.usage.editor.inherited', { value: formatTokens(m.inherited, i18n.language), source: inheritedFrom(m) })}
            ownLabel={t('admin.usage.editor.own')}
            current={m.source === 'personal' ? m.budget : null}
            busy={save.isPending}
            error={save.error}
            onSave={(value) => save.mutateAsync({ users: [{ userId: m.userId, dailyInputTokens: value }] })}
            testId="member-budget-editor"
          />
        );
      },
    },
  ];

  const bulkSave = async (value: number | null) => {
    const users = [...selected].map((userId) => ({ userId, dailyInputTokens: value }));
    await save.mutateAsync({ users, bulk: true });
    setNotice(value === null ? t('admin.usage.members.bulkReset', { count: users.length }) : t('admin.usage.members.bulkSet', { count: users.length, value: formatTokens(value, i18n.language) }));
    setSelected(new Set());
    setBulk(false);
    void queryClient.invalidateQueries({ queryKey: usageKey });
  };

  const chips: { value: UsageChip | 'all'; label: string; count?: number; tone?: 'danger' | 'warning' | 'neutral' }[] = [
    { value: 'full', label: t('admin.usage.members.chips.full'), tone: 'danger', ...(data ? { count: data.counts.full } : {}) },
    { value: 'near', label: t('admin.usage.members.chips.near'), tone: 'warning', ...(data ? { count: data.counts.near } : {}) },
    { value: 'custom', label: t('admin.usage.members.chips.custom'), ...(data ? { count: data.counts.custom } : {}) },
    { value: 'idle', label: t('admin.usage.members.chips.idle'), ...(data ? { count: data.counts.idle } : {}) },
  ];

  return (
    <div className="stack">
      <div className="dt-toolbar">
        <SearchBox value={list.get('q') ?? ''} onChange={(q) => list.set({ q })} placeholder={t('admin.usage.members.search')} testId="usage-search" />
        <FilterMenu
          label={t('admin.access.domain')}
          value={list.get('domain')}
          allLabel={t('admin.access.all')}
          options={(domains.data?.rows ?? []).map((d) => ({ value: d.id, label: d.name }))}
          onChange={(domain) => list.set({ domain })}
          testId="usage-domain"
        />
        <span className="dt-toolbar__end">
          <Menu trigger={<><Icons.sliders size={16} /> {t('admin.usage.members.columnsMenu')}</>} triggerClassName="dt-filter" align="right" data-testid="usage-columns">
            {OPTIONAL.map((c) => (
              <label key={c} className="ui-menu-item ui-check" data-keep-open="">
                <input type="checkbox" checked={chosen.has(c)} onChange={(e) => choose(c, e.target.checked)} data-testid={`usage-column-${c}`} />
                <span>{t(`admin.usage.members.columns.${c}`)}</span>
              </label>
            ))}
          </Menu>
          <CsvLink href={`/api/admin/usage/members.csv?${query}`} label={t('admin.csvExport')} testId="usage-csv" />
        </span>
      </div>
      <div className="dt-toolbar">
        <FilterChips
          label={t('admin.usage.members.chipsLabel')}
          value={(list.get('state') as UsageChip | undefined) ?? 'all'}
          onChange={(value) => list.set({ state: value === 'all' || value === null ? null : value })}
          chips={[{ value: 'all', label: t('admin.usage.members.chips.all'), ...(data ? { count: data.counts.all } : {}) }, ...chips]}
        />
      </div>
      {selected.size > 0 ? (
        <div className="bulk-bar" role="region" aria-label={t('admin.usage.members.selected', { count: selected.size })} data-testid="bulk-bar">
          <strong>{t('admin.usage.members.selected', { count: selected.size })}</strong>
          <span className="bulk-bar__actions">
            <Button size="sm" variant="secondary" onClick={() => setBulk(true)} data-testid="bulk-assign">
              {t('admin.usage.members.assign')}
            </Button>
            <Button size="sm" variant="secondary" busy={save.isPending && !bulk} onClick={() => void bulkSave(null)} data-testid="bulk-reset">
              {t('admin.usage.members.reset')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} data-testid="bulk-clear">
              {t('admin.usage.members.clear')}
            </Button>
          </span>
        </div>
      ) : null}
      {notice ? <Alert tone="success" title={notice} data-testid="usage-notice" /> : null}
      <ErrorAlert error={members.error} />
      {!data && members.isPending ? <Skeleton block /> : null}
      {data ? (
        <DataTable
          label={t('admin.usage.tabs.members')}
          columns={columns}
          rows={data.rows}
          rowKey={(m) => m.userId}
          sort={sort}
          onSort={onSort}
          selection={{
            selected,
            label: (m) => t('admin.usage.members.select', { name: m.name }),
            onToggle: (key, on) => {
              const next = new Set(selected);
              if (on) next.add(key);
              else next.delete(key);
              setSelected(next);
            },
            onTogglePage: (on) => {
              const next = new Set(selected);
              for (const m of data.rows) {
                if (!editable(m)) continue;
                if (on) next.add(m.userId);
                else next.delete(m.userId);
              }
              setSelected(next);
            },
          }}
          rowTestId={(m) => `usage-member-${m.email}`}
          loading={members.isFetching}
          data-testid="usage-members-table"
          footer={
            <Pagination
              page={list.page}
              size={list.size}
              total={data.total}
              noun={t('admin.usage.members.noun')}
              note={sort.key === 'percent' && sort.desc ? t('admin.usage.members.note') : undefined}
              onPage={(page) => list.set({ page: String(page) })}
              onSize={(size) => list.set({ size: String(size) })}
            />
          }
        />
      ) : null}
      {bulk ? <BulkDialog count={selected.size} busy={save.isPending} error={save.error} onSave={bulkSave} onClose={() => setBulk(false)} /> : null}
    </div>
  );
}

function BulkDialog({ count, busy, error, onSave, onClose }: { count: number; busy: boolean; error: unknown; onSave: (value: number) => Promise<void>; onClose: () => void }) {
  const { t } = useTranslation();
  const [digits, setDigits] = useState('');
  return (
    <Dialog title={t('admin.usage.editor.bulkTitle', { count })} closeLabel={t('common.close')} onClose={onClose} data-testid="bulk-dialog">
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          void onSave(Number(digits));
        }}
      >
        <TokenInput value={digits} onChange={setDigits} label={t('admin.usage.editor.own')} testId="bulk-value" />
        <p className="muted">{t('admin.usage.editor.bulkHint')}</p>
        <ErrorAlert error={error} />
        <div className="row">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" busy={busy} disabled={Number(digits) < 1000} data-testid="bulk-save">
            {t('common.save')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function WorkspacesTab() {
  const { t, i18n } = useTranslation();
  const list = useListParams<'domain' | 'kind'>();
  const { sort, onSort } = sortState(list, 'input', (key) => DESC.has(key));
  const query = list.query();
  const spaces = useQuery({ queryKey: [...usageKey, 'workspaces', query], queryFn: () => api<UsageWorkspacesDto>('GET', `/api/admin/usage/workspaces?${query}`), placeholderData: keepPreviousData });
  const domains = useAdminDomains();
  const data = spaces.data;
  const money = (micros: number) => formatMoney(micros, data?.currency ?? 'TRY', i18n.language, data?.pricesSet ?? false);
  const t0 = data?.totals;
  const columns: Column<UsageWorkspaceDto>[] = [
    {
      id: 'workspace',
      header: t('admin.usage.workspaces.columns.workspace'),
      sort: 'name',
      cell: (w) => (
        <span className="person-cell">
          <span className="person-cell__name">{w.name}</span>
          <span className="person-cell__email">{w.domain?.name ?? w.owner.name}</span>
        </span>
      ),
    },
    {
      id: 'kind',
      header: t('admin.usage.workspaces.kind'),
      width: 'md',
      cell: (w) => <span className={`kind-chip kind-chip--${w.kind}`}>{t(`admin.usage.workspaces.kinds.${w.kind}`)}</span>,
    },
    { id: 'input', header: t('admin.usage.members.columns.input'), width: 'sm', numeric: true, sort: 'input', cell: (w) => formatTokens(w.input, i18n.language), total: t0 ? formatTokens(t0.input, i18n.language) : null },
    { id: 'output', header: t('admin.usage.members.columns.output'), width: 'sm', numeric: true, sort: 'output', cell: (w) => formatTokens(w.output, i18n.language), total: t0 ? formatTokens(t0.output, i18n.language) : null },
    { id: 'calls', header: t('admin.usage.members.columns.calls'), width: 'sm', numeric: true, sort: 'calls', cell: (w) => formatCount(w.calls, i18n.language), total: t0 ? formatCount(t0.calls, i18n.language) : null },
    { id: 'runs', header: t('admin.usage.members.columns.runs'), width: 'xs', numeric: true, sort: 'runs', cell: (w) => formatCount(w.runs, i18n.language), total: t0 ? formatCount(t0.runs, i18n.language) : null },
    { id: 'sandbox', header: t('admin.usage.members.columns.sandbox'), width: 'sm', numeric: true, sort: 'sandbox', cell: (w) => formatSeconds(w.sandboxSeconds, i18n.language), total: t0 ? formatSeconds(t0.sandboxSeconds, i18n.language) : null },
    { id: 'storage', header: t('admin.usage.members.columns.storage'), width: 'sm', numeric: true, sort: 'storage', cell: (w) => formatBytes(w.storageBytes, i18n.language), total: t0 ? formatBytes(t0.storageBytes, i18n.language) : null },
    { id: 'cost', header: t('admin.usage.members.columns.cost'), width: 'sm', numeric: true, sort: 'cost', cell: (w) => money(w.costMicros), total: t0 ? money(t0.costMicros) : null },
  ];
  return (
    <div className="stack">
      <div className="dt-toolbar">
        <SearchBox value={list.get('q') ?? ''} onChange={(q) => list.set({ q })} placeholder={t('admin.usage.workspaces.search')} testId="workspaces-search" />
        <FilterMenu
          label={t('admin.access.domain')}
          value={list.get('domain')}
          allLabel={t('admin.access.all')}
          options={(domains.data?.rows ?? []).map((d) => ({ value: d.id, label: d.name }))}
          onChange={(domain) => list.set({ domain })}
          testId="workspaces-domain"
        />
        <FilterMenu
          label={t('admin.usage.workspaces.kind')}
          value={list.get('kind')}
          allLabel={t('admin.access.all')}
          options={(['chat', 'code'] as const).map((kind) => ({ value: kind, label: t(`admin.usage.workspaces.kinds.${kind}`) }))}
          onChange={(kind) => list.set({ kind })}
          testId="workspaces-kind"
        />
        <span className="dt-toolbar__end">
          <CsvLink href={`/api/admin/usage/workspaces.csv?${query}`} label={t('admin.csv')} testId="workspaces-csv" />
        </span>
      </div>
      <ErrorAlert error={spaces.error} />
      {!data && spaces.isPending ? <Skeleton block /> : null}
      {data ? (
        <DataTable
          label={t('admin.usage.tabs.workspaces')}
          columns={columns}
          rows={data.rows}
          rowKey={(w) => w.id}
          sort={sort}
          onSort={onSort}
          totalLabel={<strong>{t('admin.usage.workspaces.total', { count: data.total })}</strong>}
          rowTestId={(w) => `usage-workspace-${w.id}`}
          rowClassName={(w) => (w.input === 0 && w.output === 0 ? 'dt__row--quiet' : undefined)}
          loading={spaces.isFetching}
          empty={<EmptyState>{t('usage.empty')}</EmptyState>}
          data-testid="usage-workspaces-table"
          footer={
            <Pagination
              page={list.page}
              size={list.size}
              total={data.total}
              noun={t('admin.usage.workspaces.noun')}
              note={sort.key === 'input' && sort.desc ? t('admin.usage.members.note') : undefined}
              onPage={(page) => list.set({ page: String(page) })}
              onSize={(size) => list.set({ size: String(size) })}
            />
          }
        />
      ) : null}
      <p className="muted" data-testid="workspaces-cost-note">
        {data?.pricesSet ? t('admin.usage.workspaces.costNote') : t('admin.usage.workspaces.noPrices')}
      </p>
    </div>
  );
}
